import type {
  AccessDeviceRegistryItem,
  AccessDeviceRegistrySortBy,
  AccessDeviceSortDirection,
} from '@/api/core/access-device-registry';
import { canWriteProperties } from '@/api/core/authorization';
import type {
  CoreAccessDeviceInventoryItem,
  CoreAccessDeviceInventoryPage,
} from '@/api/core/core-access-device-inventory';
import {
  PROPERTY_PAGE_LIMIT,
  type CatalogRoom,
  type CatalogSlot,
  type PropertySummary,
} from '@/api/core/properties';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { IdText } from '@/components/id-text';
import { RowLink } from '@/components/row-link';
import { SearchField } from '@/components/search-field';
import { isUuid } from '@/lib/validation';
import {
  Badge,
  Button,
  DataTable,
  Input,
  ListPage,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useInsertionEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  accessDeviceRegistryQuery,
  coreAccessDeviceInventoryQuery,
  propertiesQuery,
  propertyCatalogQuery,
  propertyRuntime,
  roomOnlineAccessQuery,
} from '../property/property-queries';
import { canonicalSlotName } from '../property/rooms/room-catalog-section';
import { AccessDeviceCreateForm } from './access-device-create-form';
import { AccessDeviceMetadataEditForm } from './access-device-metadata-edit-form';
import { AccessDeviceRetirementForm } from './access-device-retirement-form';
import { AccessDeviceSlotLinksForm } from './access-device-slot-links-form';
import { accessHref, accessUrlState, deviceLedgerState } from './access-url';
import { DevicePurposeEditor } from './device-purpose-editor';
import { RoomOnlineAccessSection } from './room-online-access-section';

function AccessFailure({
  error,
  onRetry,
  busy = false,
  message,
}: {
  error: unknown;
  onRetry: () => void;
  busy?: boolean;
  message?: string;
}) {
  const title =
    message ??
    (isApiError(error) && error.status === 403
      ? '출입 관리 조회 권한이 없습니다'
      : isApiError(error) && error.status === 404
        ? '지점 또는 객실을 찾을 수 없습니다'
        : isApiError(error) && error.kind === 'invalid-response'
          ? '응답 자료를 확인하지 못했습니다'
          : '출입 자료를 불러오지 못했습니다');
  return (
    <div role="alert" className="flex flex-col items-start gap-3 p-4">
      <p>{title}</p>
      {isApiError(error) && (error.status === 403 || error.status === 404) ? null : (
        <Button variant="outline" disabled={busy} onClick={onRetry}>
          다시 조회
        </Button>
      )}
    </div>
  );
}

function AccessViewSwitch({
  active,
  onSelect,
  disabled = false,
}: {
  active: 'property' | 'device';
  onSelect: (view: 'property' | 'device') => void;
  disabled?: boolean;
}) {
  const views = [
    ['property', '지점별 조회'],
    ['device', '장비 목록'],
  ] as const;
  return (
    <nav aria-label="출입 관리 조회 방식" className="flex gap-2">
      {views.map(([view, label]) => (
        <Button
          key={view}
          variant={view === active ? 'solid' : 'outline'}
          aria-current={view === active ? 'page' : undefined}
          disabled={disabled && view !== active}
          onClick={() => {
            if (view !== active) onSelect(view);
          }}
        >
          {label}
        </Button>
      ))}
    </nav>
  );
}

function PropertyListModeControl({ onShowDeviceLedger }: { onShowDeviceLedger: () => void }) {
  return (
    <div className="flex justify-end border-b p-4">
      <AccessViewSwitch
        active="property"
        onSelect={() => {
          onShowDeviceLedger();
        }}
      />
    </div>
  );
}

const activeRoomRead = new WeakMap<
  typeof propertyRuntime.session,
  { propertyId: string; roomId: string | null; roomCursor: string | null; generation: number }
>();
interface ActiveInventoryRead {
  apiNamespace: string;
  propertyId: string;
  environment: 'dev' | 'prod';
  branchId: number;
  roomId: string;
  catalogCursor: string | null;
  slotId: string | null;
  cursor: string | null;
  generation: number;
}
const activeInventoryRead = new WeakMap<typeof propertyRuntime.session, ActiveInventoryRead>();
interface ActiveRegistryRead {
  apiNamespace: string;
  generation: number;
  cursor: string | null;
  sort: string | null;
}
const activeRegistryRead = new WeakMap<typeof propertyRuntime.session, ActiveRegistryRead>();

function inventoryStateLabel(state: CoreAccessDeviceInventoryItem['state']): string {
  switch (state) {
    case 'OBSERVED':
      return '장비 연결 확인';
    case 'NO_MAPPING':
      return '연결 기록 없음';
    case 'SOURCE_MISSING':
      return '연결 확인 불가';
    case 'SOURCE_MISMATCH':
      return '공간 정보 확인 필요';
    case 'SOURCE_DELETED':
      return '삭제 표시 확인 필요';
  }
}

function inventoryBranchStateLabel(
  state: CoreAccessDeviceInventoryPage['source_state'],
): string | null {
  if (state === 'SOURCE_MISSING') return '지점 연결 정보 확인 불가';
  if (state === 'SOURCE_DELETED') return '지점 삭제 표시 확인 필요';
  return null;
}

function safeAccessListReturn(value: unknown): string {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  )
    return '/access';
  try {
    const url = new URL(value, 'https://access.invalid');
    const state = accessUrlState(url.searchParams);
    return url.origin === 'https://access.invalid' &&
      url.pathname === '/access' &&
      !url.hash &&
      state.valid &&
      state.roomId === null &&
      state.roomCursor === null
      ? url.pathname + url.search
      : '/access';
  } catch {
    return '/access';
  }
}

// 이름과 ID가 같으면 한 번만 보이고, UUID는 앞 8자리로 줄인다(전체 값은 title·상세 화면에서 확인).
function compactId(id: string): string {
  return isUuid(id) ? `${id.slice(0, 8)}…` : id;
}

function nameWithId(name: string | null | undefined, id: string): string {
  return name && name !== id ? `${name} · ${compactId(id)}` : compactId(id);
}

type BadgeTone = 'neutral' | 'danger' | 'warning' | 'success' | 'info';

function registryResolutionTone(
  state: AccessDeviceRegistryItem['connections'][number]['resolution_state'],
): BadgeTone {
  switch (state) {
    case 'RESOLVED':
      return 'success';
    case 'MISSING':
      return 'danger';
    case 'DELETED':
    case 'UNCERTAIN':
      return 'warning';
  }
}

function registryResolutionLabel(
  state: AccessDeviceRegistryItem['connections'][number]['resolution_state'],
) {
  switch (state) {
    case 'RESOLVED':
      return '공간 연결 확인됨';
    case 'MISSING':
      return '공간 정보 미존재';
    case 'DELETED':
      return '삭제된 공간';
    case 'UNCERTAIN':
      return '공간 확인 필요';
  }
}

export function AccessDeviceLedger({
  runtime,
  generation,
  onBack,
}: {
  runtime: typeof propertyRuntime;
  generation: number;
  onBack: () => void;
}) {
  const session = useOperatorSession(runtime.session);
  const canCreate =
    session.status === 'authorized' &&
    session.generation === generation &&
    Boolean(session.authorization && canWriteProperties(session.authorization));
  const ledgerScope = `${runtime.apiNamespace}:${String(generation)}`;
  const previousLedgerScope = useRef(ledgerScope);
  const scopeChanged = previousLedgerScope.current !== ledgerScope;
  const [ledgerParams, setLedgerParams] = useSearchParams();
  const ledgerState = deviceLedgerState(ledgerParams);
  const sort = ledgerState.sort;
  const updateLedger = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(ledgerParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    setLedgerParams(next);
  };
  const [pageState, setPageState] = useState<{ scope: string; cursors: (string | null)[] }>({
    scope: ledgerScope,
    cursors: [null],
  });
  const cursors = pageState.scope === ledgerScope ? pageState.cursors : [null];
  const search = ledgerState.search;
  const setSearch = (value: string) => {
    updateLedger({ device_q: value || null });
  };
  const selected =
    !scopeChanged && ledgerState.deviceId ? { scope: ledgerScope, id: ledgerState.deviceId } : null;
  const setSelected = (value: { scope: string; id: string } | null) => {
    updateLedger({ device: value?.id ?? null });
  };
  const [showCreate, setShowCreate] = useState(false);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [linkingDeviceId, setLinkingDeviceId] = useState<string | null>(null);
  const [retiringDevice, setRetiringDevice] = useState<AccessDeviceRegistryItem | null>(null);
  const [slotLinkReceipt, setSlotLinkReceipt] = useState<{
    apiNamespace: string;
    generation: number;
    deviceId: string;
    message: string;
  } | null>(null);
  const cursor = scopeChanged ? null : ledgerState.cursor;
  const cursorIndex = cursors.lastIndexOf(cursor);
  const currentCursors = cursorIndex >= 0 ? cursors.slice(0, cursorIndex + 1) : [null, cursor];
  useEffect(() => {
    if (previousLedgerScope.current === ledgerScope) return;
    // Keep ignoring the previous scope's URL state until the reset has committed.
    if (ledgerState.cursor === null && ledgerState.deviceId === null) {
      previousLedgerScope.current = ledgerScope;
      return;
    }
    const next = new URLSearchParams(ledgerParams);
    next.delete('device_cursor');
    next.delete('device');
    queueMicrotask(() => {
      setLedgerParams(next, { replace: true });
    });
  }, [ledgerScope, ledgerParams, ledgerState.cursor, ledgerState.deviceId, setLedgerParams]);
  const currentRegistryRead: ActiveRegistryRead = {
    apiNamespace: runtime.apiNamespace,
    generation,
    cursor,
    sort,
  };
  useInsertionEffect(() => {
    activeRegistryRead.set(runtime.session, currentRegistryRead);
    return () => {
      if (activeRegistryRead.get(runtime.session) === currentRegistryRead)
        activeRegistryRead.delete(runtime.session);
    };
  }, [runtime.session, runtime.apiNamespace, generation, cursor, sort]);
  const registry = useQuery(
    accessDeviceRegistryQuery(
      runtime,
      generation,
      {
        cursor: cursor ?? undefined,
        limit: 50,
        ...(sort
          ? {
              sortBy: sort.split(',')[0] as AccessDeviceRegistrySortBy,
              sortDirection: sort.split(',')[1] as AccessDeviceSortDirection,
            }
          : {}),
      },
      ledgerState.valid,
      () => {
        const current = activeRegistryRead.get(runtime.session);
        return (
          current?.apiNamespace === runtime.apiNamespace &&
          current.cursor === cursor &&
          current.sort === sort &&
          current.generation === generation
        );
      },
    ),
  );
  const rows = registry.data?.items ?? [];
  const normalizedSearch = search.toLocaleLowerCase();
  const visibleRows = rows.filter((item) =>
    [item.device_name, item.device_id, item.gateway_name, item.gateway_id, item.device_type].some(
      (value) => value?.toLocaleLowerCase().includes(normalizedSearch),
    ),
  );
  const selectedDevice =
    selected?.scope === ledgerScope
      ? (rows.find((item) => item.core_device_id === selected.id) ?? null)
      : null;
  const refreshFlight = useRef<Promise<AccessDeviceRegistryItem[] | undefined> | null>(null);
  const refresh = async (deviceId?: string) => {
    const flight =
      refreshFlight.current ??
      (async () => {
        const result = await registry.refetch({ cancelRefetch: false });
        return result.isError ? undefined : result.data?.items;
      })();
    refreshFlight.current = flight;
    try {
      const items = await flight;
      return deviceId ? items?.find((item) => item.core_device_id === deviceId) : undefined;
    } finally {
      if (refreshFlight.current === flight) refreshFlight.current = null;
    }
  };
  if (!ledgerState.valid)
    return (
      <div role="alert" className="space-y-3 p-4">
        <p>장비 목록 주소를 확인해 주세요.</p>
        <Button
          onClick={() => {
            updateLedger({
              device_sort: null,
              device_direction: null,
              device_cursor: null,
              device: null,
            });
          }}
        >
          장비 목록 처음으로
        </Button>
      </div>
    );
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <div>
          <p className="body-strong">장비 목록</p>
          <p className="caption text-muted-foreground mt-1">
            현재 페이지 기준 조회입니다. 장비 연결 상태나 실제 작동은 확인하지 않습니다.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <AccessViewSwitch
            active="device"
            disabled={
              showCreate ||
              editingDeviceId !== null ||
              linkingDeviceId !== null ||
              retiringDevice !== null
            }
            onSelect={onBack}
          />
          {canCreate &&
            !showCreate &&
            editingDeviceId === null &&
            linkingDeviceId === null &&
            retiringDevice === null && (
              <Button
                tone="brand"
                onClick={() => {
                  setShowCreate(true);
                }}
              >
                새 장비 등록
              </Button>
            )}
          <Button
            variant="outline"
            disabled={
              registry.isFetching ||
              showCreate ||
              editingDeviceId !== null ||
              linkingDeviceId !== null ||
              retiringDevice !== null
            }
            onClick={() => void refresh()}
          >
            {registry.isFetching ? '확인 중' : '다시 조회'}
          </Button>
        </div>
      </div>
      <div className="space-y-4 p-4 sm:p-6">
        {showCreate && (
          <AccessDeviceCreateForm
            key={ledgerScope}
            runtime={runtime}
            generation={generation}
            onClose={() => {
              setShowCreate(false);
            }}
          />
        )}
        {selectedDevice?.core_device_id === editingDeviceId && (
          <AccessDeviceMetadataEditForm
            key={`${ledgerScope}:${selectedDevice.core_device_id}`}
            runtime={runtime}
            generation={generation}
            device={selectedDevice}
            onClose={() => {
              setEditingDeviceId(null);
            }}
            refreshRegistry={() => refresh(selectedDevice.core_device_id)}
          />
        )}
        {selectedDevice?.core_device_id === linkingDeviceId && (
          <AccessDeviceSlotLinksForm
            key={`${ledgerScope}:${selectedDevice.core_device_id}`}
            runtime={runtime}
            generation={generation}
            device={selectedDevice}
            onClose={() => {
              setLinkingDeviceId(null);
            }}
            onSaved={(message) => {
              setSlotLinkReceipt({
                apiNamespace: runtime.apiNamespace,
                generation,
                deviceId: selectedDevice.device_id,
                message,
              });
            }}
            refreshRegistry={() => refresh(selectedDevice.core_device_id)}
          />
        )}
        {retiringDevice && (
          <AccessDeviceRetirementForm
            key={`${ledgerScope}:${retiringDevice.core_device_id}`}
            runtime={runtime}
            generation={generation}
            device={retiringDevice}
            onClose={() => {
              setRetiringDevice(null);
            }}
            refreshRegistry={() => refresh(retiringDevice.core_device_id)}
            refreshRegistryAfterSave={async () => {
              const result = await registry.refetch({ cancelRefetch: false });
              return { failed: result.isError };
            }}
          />
        )}
        {slotLinkReceipt?.apiNamespace === runtime.apiNamespace &&
          slotLinkReceipt.generation === generation && (
            <p
              role="status"
              aria-label="연결 변경 저장됨"
              className="body-sm rounded-md border p-3"
            >
              장비 {slotLinkReceipt.deviceId} · {slotLinkReceipt.message}
            </p>
          )}
        <label className="flex flex-col gap-1">
          <span className="caption">현재 페이지에서 장비 찾기</span>
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="장비 이름 또는 ID, 게이트웨이 이름 또는 ID"
            disabled={
              registry.isFetching ||
              editingDeviceId !== null ||
              linkingDeviceId !== null ||
              retiringDevice !== null
            }
          />
        </label>
        <p className="caption text-muted-foreground">
          {registry.data
            ? `현재 페이지 ${String(rows.length)}개 · ${new Date(registry.dataUpdatedAt).toLocaleString('ko-KR')} 조회`
            : '조회 대기'}{' '}
          · 공간 이름은 확인된 연결 정보만 표시합니다.
        </p>
        {registry.isFetching && registry.data && (
          <p role="status" className="caption text-muted-foreground">
            새 페이지를 확인하고 있습니다.
          </p>
        )}
        {registry.isError ? (
          isApiError(registry.error) && registry.error.status === 400 && cursor ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border p-3">
              <p>조회 위치를 확인하지 못했습니다. 첫 페이지부터 다시 조회해 주세요.</p>
              <Button
                variant="outline"
                disabled={registry.isFetching}
                onClick={() => {
                  setPageState({ scope: ledgerScope, cursors: [null] });
                  updateLedger({ device_cursor: null, device: null });
                }}
              >
                첫 페이지부터 다시 조회
              </Button>
            </div>
          ) : (
            <AccessFailure
              error={registry.error}
              onRetry={() => {
                void refresh();
              }}
              busy={registry.isFetching}
            />
          )
        ) : registry.isPending ? (
          <p role="status" className="body text-muted-foreground">
            장비 목록을 불러오고 있습니다.
          </p>
        ) : (
          <>
            <p
              id="device-ledger-scroll-hint"
              className="caption text-muted-foreground mb-3 sm:hidden"
            >
              표는 좌우로 스크롤할 수 있습니다.
            </p>
            <div
              className="min-w-0 overflow-x-auto"
              role="region"
              aria-label="장비 목록"
              aria-describedby="device-ledger-scroll-hint"
              tabIndex={0}
            >
              <div className="min-w-[48rem]">
                <DataTable
                  sort={sort}
                  onSortChange={
                    editingDeviceId !== null ||
                    linkingDeviceId !== null ||
                    showCreate ||
                    retiringDevice !== null
                      ? undefined
                      : (nextSort) => {
                          updateLedger({
                            device_sort: nextSort?.split(',')[0] ?? null,
                            device_direction: nextSort?.split(',')[1] ?? null,
                            device_cursor: null,
                            device: null,
                          });
                          setPageState({ scope: ledgerScope, cursors: [null] });
                        }
                  }
                  columns={
                    [
                      {
                        key: 'device_name',
                        sortable: true,
                        header: '장비',
                        cell: (item: AccessDeviceRegistryItem) => (
                          <button
                            type="button"
                            className="text-primary underline-offset-4 hover:underline"
                            title={item.device_id}
                            onClick={() => {
                              setSelected({ scope: ledgerScope, id: item.core_device_id });
                            }}
                            disabled={
                              editingDeviceId !== null ||
                              linkingDeviceId !== null ||
                              showCreate ||
                              retiringDevice !== null
                            }
                          >
                            {nameWithId(item.device_name, item.device_id)}
                          </button>
                        ),
                      },
                      {
                        key: 'gateway_name',
                        sortable: true,
                        header: '게이트웨이',
                        cell: (item: AccessDeviceRegistryItem) => (
                          <span title={item.gateway_id}>
                            {nameWithId(item.gateway_name, item.gateway_id)}
                          </span>
                        ),
                      },
                      {
                        key: 'device_type',
                        sortable: true,
                        header: '유형',
                        cell: (item: AccessDeviceRegistryItem) => item.device_type ?? '정보 없음',
                      },
                      {
                        key: 'registration_property',
                        sortable: false,
                        header: '등록 지점',
                        cell: (item: AccessDeviceRegistryItem) => (
                          <span className="break-all">
                            {item.registration_property_id ?? '미확인'}
                          </span>
                        ),
                      },
                      {
                        key: 'links',
                        sortable: false,
                        header: '공간 연결',
                        cell: (item: AccessDeviceRegistryItem) =>
                          `${String(item.connections.length)}개 연결`,
                      },
                    ] satisfies DataTableColumn<AccessDeviceRegistryItem>[]
                  }
                  data={visibleRows}
                  rowKey={(item) => item.core_device_id}
                  empty={{
                    title: search
                      ? '현재 페이지에 조건에 맞는 장비가 없습니다'
                      : cursor
                        ? '다음 페이지에 장비가 없습니다'
                        : '등록된 장비가 없습니다',
                  }}
                />
              </div>
            </div>
            <nav aria-label="Core 장비 페이지 이동" className="flex flex-wrap gap-2">
              {cursor && (
                <Button
                  variant="outline"
                  disabled={
                    registry.isFetching ||
                    editingDeviceId !== null ||
                    linkingDeviceId !== null ||
                    showCreate ||
                    retiringDevice !== null
                  }
                  onClick={() => {
                    updateLedger({ device: null, device_cursor: currentCursors.at(-2) ?? null });
                    setPageState({ scope: ledgerScope, cursors: currentCursors.slice(0, -1) });
                  }}
                >
                  이전 페이지
                </Button>
              )}
              {registry.data.next_cursor !== null && (
                <Button
                  variant="outline"
                  disabled={
                    registry.isFetching ||
                    editingDeviceId !== null ||
                    linkingDeviceId !== null ||
                    showCreate ||
                    retiringDevice !== null
                  }
                  onClick={() => {
                    const next = registry.data.next_cursor;
                    if (next) {
                      updateLedger({ device: null, device_cursor: next });
                      setPageState({ scope: ledgerScope, cursors: [...currentCursors, next] });
                    }
                  }}
                >
                  다음 페이지
                </Button>
              )}
            </nav>
            {selectedDevice && (
              <section aria-label="장비 정보" className="space-y-3 rounded-md border p-4">
                <DevicePurposeEditor
                  key={selectedDevice.core_device_id}
                  runtime={runtime}
                  id={selectedDevice.core_device_id}
                  deviceRevision={selectedDevice.revision}
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="title-sm">
                    장비 정보 ·{' '}
                    {(selectedDevice.device_name?.length ?? 0) > 0
                      ? selectedDevice.device_name
                      : selectedDevice.device_id}
                  </h2>
                  <Button
                    variant="outline"
                    disabled={
                      editingDeviceId !== null ||
                      linkingDeviceId !== null ||
                      retiringDevice !== null
                    }
                    onClick={() => {
                      setSelected(null);
                    }}
                  >
                    닫기
                  </Button>
                </div>
                <dl className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <dt className="caption text-muted-foreground">장비 ID</dt>
                    <dd className="body break-all">{selectedDevice.device_id}</dd>
                  </div>
                  <div>
                    <dt className="caption text-muted-foreground">게이트웨이</dt>
                    <dd className="body break-all">
                      {selectedDevice.gateway_name ?? '이름 미제공'} · {selectedDevice.gateway_id}
                    </dd>
                  </div>
                  <div>
                    <dt className="caption text-muted-foreground">장비 유형</dt>
                    <dd className="body">{selectedDevice.device_type ?? '정보 없음'}</dd>
                  </div>
                  <div>
                    <dt className="caption text-muted-foreground">등록 지점</dt>
                    <dd className="body break-all">
                      {selectedDevice.registration_property_id ?? '미확인'}
                    </dd>
                  </div>
                  <div>
                    <dt className="caption text-muted-foreground">저장된 활성 설정</dt>
                    <dd className="body">
                      {selectedDevice.is_active ? '활성' : '비활성'} · 연결 상태 미확인
                    </dd>
                  </div>
                  <div>
                    <dt className="caption text-muted-foreground">저장된 배터리</dt>
                    <dd className="body">
                      {selectedDevice.battery === null
                        ? '정보 없음'
                        : String(selectedDevice.battery)}{' '}
                      · 현재 잔량 미확인
                    </dd>
                  </div>
                </dl>
                <div>
                  <h3 className="body-strong">연결 공간 ({selectedDevice.connections.length}개)</h3>
                  {selectedDevice.connections.length ? (
                    <ul className="mt-2 space-y-2">
                      {selectedDevice.connections.map((connection) => (
                        <li key={connection.slot_id} className="rounded border p-3">
                          <p className="body-strong">
                            <Badge tone={registryResolutionTone(connection.resolution_state)}>
                              {registryResolutionLabel(connection.resolution_state)}
                            </Badge>
                          </p>
                          {connection.resolution_state === 'RESOLVED' && (
                            <dl className="mt-3 grid gap-2 sm:grid-cols-3">
                              <div>
                                <dt className="caption text-muted-foreground">지점</dt>
                                <dd className="body break-words">
                                  {connection.property_name ?? '지점명 미등록'}
                                </dd>
                              </div>
                              <div>
                                <dt className="caption text-muted-foreground">호실</dt>
                                <dd className="body">
                                  {connection.room_no === null
                                    ? '호실 번호 미등록'
                                    : `호실 ${connection.room_no}`}
                                </dd>
                              </div>
                              <div>
                                <dt className="caption text-muted-foreground">슬롯 번호</dt>
                                <dd className="body break-words">
                                  {connection.slot_no === null
                                    ? '슬롯 번호 미등록'
                                    : connection.slot_no === ''
                                      ? '빈 값'
                                      : connection.slot_no}
                                </dd>
                              </div>
                            </dl>
                          )}
                          <details className="caption mt-1">
                            <summary className="cursor-pointer">기술 정보</summary>
                            <p className="break-all">슬롯 UUID {connection.slot_id}</p>
                            {connection.property_id && (
                              <p className="break-all">지점 UUID {connection.property_id}</p>
                            )}
                            {connection.room_id && (
                              <p className="break-all">호실 UUID {connection.room_id}</p>
                            )}
                          </details>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="body text-muted-foreground mt-1">현재 연결 기록이 없습니다.</p>
                  )}
                </div>
                {canCreate &&
                  editingDeviceId === null &&
                  linkingDeviceId === null &&
                  !showCreate &&
                  retiringDevice === null && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingDeviceId(selectedDevice.core_device_id);
                      }}
                    >
                      기존 장비 정보 수정
                    </Button>
                  )}
                {canCreate &&
                  editingDeviceId === null &&
                  linkingDeviceId === null &&
                  !showCreate &&
                  retiringDevice === null &&
                  !selectedDevice.retired && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelected({ scope: ledgerScope, id: selectedDevice.core_device_id });
                        setLinkingDeviceId(selectedDevice.core_device_id);
                      }}
                    >
                      기존 장비 연결 관리
                    </Button>
                  )}
                {canCreate &&
                  editingDeviceId === null &&
                  linkingDeviceId === null &&
                  !showCreate &&
                  retiringDevice === null &&
                  !selectedDevice.retired && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setRetiringDevice(selectedDevice);
                      }}
                    >
                      기존 장비 퇴역
                    </Button>
                  )}
                <details className="caption">
                  <summary className="cursor-pointer">기술 정보</summary>
                  <dl className="mt-2 grid gap-1 break-all">
                    <div>
                      <dt>Core UUID</dt>
                      <dd>{selectedDevice.core_device_id}</dd>
                    </div>
                    <div>
                      <dt>메타데이터 revision</dt>
                      <dd>{selectedDevice.revision}</dd>
                    </div>
                    <div>
                      <dt>Core 저장 시각</dt>
                      <dd>{selectedDevice.updated_at}</dd>
                    </div>
                  </dl>
                </details>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

const ACCESS_LIST_NEXT_STATE = { accessListNext: true };

export function AccessPropertyListPage({
  runtime = propertyRuntime,
}: { runtime?: typeof propertyRuntime } = {}) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const [propertySearch, setPropertySearch] = useState('');
  const [params, setParams] = useSearchParams();
  const showDeviceLedger = params.get('view') === 'devices';
  const setShowDeviceLedger = (show: boolean) => {
    const next = new URLSearchParams(params);
    if (show) next.set('view', 'devices');
    else {
      next.delete('view');
      next.delete('device_cursor');
      next.delete('device');
    }
    setParams(next, { replace: true });
  };
  const location = useLocation();
  const navigate = useNavigate();
  // 다음으로 넘어온 경우에만 이전이 브라우저 기록으로 돌아간다. 주소로 바로 들어온 페이지는 처음으로 보낸다.
  const cameFromList =
    typeof location.state === 'object' &&
    location.state !== null &&
    'accessListNext' in location.state;
  const state = accessUrlState(params);
  const validListState = state.valid && state.roomId === null && state.roomCursor === null;
  const list = useQuery(
    propertiesQuery(
      runtime,
      session.generation,
      state.propertyCursor,
      validListState && session.status === 'authorized',
    ),
  );
  useEffect(() => {
    const legacyKey = ['core', runtime.apiNamespace, session.generation, 'access-device-inventory'];
    const matchesLegacy = (query: { queryKey: readonly unknown[] }) =>
      legacyKey.every((part, index) => query.queryKey[index] === part);
    void queryClient.cancelQueries({ predicate: matchesLegacy });
    queryClient.removeQueries({ predicate: matchesLegacy });
  }, [queryClient, runtime.apiNamespace, session.generation, showDeviceLedger]);
  if (session.status !== 'authorized') return null;
  if (showDeviceLedger)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader
            title="출입 관리"
            description="등록된 출입 장비와 공간 연결 상태를 확인합니다."
            className="items-start sm:items-center"
          />
        </ShellHeader>
        <ListPage.Body className="min-w-0">
          <AccessDeviceLedger
            runtime={runtime}
            generation={session.generation}
            onBack={() => {
              setShowDeviceLedger(false);
            }}
          />
        </ListPage.Body>
      </ListPage>
    );
  const rows = list.data?.items ?? [];
  const normalizedPropertySearch = propertySearch.trim().toLocaleLowerCase();
  const visibleProperties = normalizedPropertySearch
    ? rows.filter((property) =>
        `${property.name} ${property.code}`.toLocaleLowerCase().includes(normalizedPropertySearch),
      )
    : rows;
  const last = list.data?.nextLookup;
  const next = () => {
    if (!last) return;
    setParams(new URLSearchParams(last as unknown as Record<string, string>), {
      state: ACCESS_LIST_NEXT_STATE,
    });
  };
  if (!validListState)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <p role="alert" className="p-6">
          조회 주소가 올바르지 않습니다. 지점 목록을 다시 열어 주세요.
        </p>
      </ListPage>
    );
  if (list.isError)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <PropertyListModeControl
          onShowDeviceLedger={() => {
            setShowDeviceLedger(true);
          }}
        />
        <AccessFailure
          error={list.error}
          onRetry={() => {
            void list.refetch();
          }}
        />
      </ListPage>
    );
  if (list.isPending)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <PropertyListModeControl
          onShowDeviceLedger={() => {
            setShowDeviceLedger(true);
          }}
        />
        <p role="status" className="p-6">
          지점 목록을 불러오고 있습니다.
        </p>
      </ListPage>
    );
  const returnTo = location.pathname + location.search;
  // 서버가 has_more를 주지 않아, 한 페이지를 꽉 채운 경우에만 다음이 있을 수 있다고 본다(지점 목록과 같은 규칙).
  const hasNext = Boolean(last) && rows.length >= PROPERTY_PAGE_LIMIT;
  const hasPrevious = state.propertyCursor !== null;
  const goDetail = (propertyId: string) => {
    void navigate(accessHref(propertyId), { state: { from: returnTo } });
  };
  const columns: DataTableColumn<PropertySummary>[] = [
    {
      key: 'name',
      sortable: false,
      header: '지점명',
      cell: (property) => (
        <RowLink to={accessHref(property.property_id)} state={{ from: returnTo }}>
          {property.name}
        </RowLink>
      ),
    },
    {
      key: 'environment',
      sortable: false,
      header: '환경',
      cell: (property) => (
        <Badge tone={property.environment === 'prod' ? 'warning' : 'neutral'}>
          {property.environment}
        </Badge>
      ),
    },
    { key: 'code', sortable: false, header: '코드', cell: (property) => property.code },
    {
      key: 'property_id',
      sortable: false,
      header: '통합 ID',
      cell: (property) => <IdText value={property.property_id} label="통합 지점 ID" />,
    },
  ];
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title="출입 관리"
          description="지점을 선택해 객실과 저장된 온라인 출입 설정을 확인합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      <ListPage.Toolbar
        left={
          <div className="flex w-full flex-wrap items-center gap-3">
            <AccessViewSwitch
              active="property"
              onSelect={() => {
                setShowDeviceLedger(true);
              }}
            />
            <SearchField
              value={propertySearch}
              onSearch={setPropertySearch}
              placeholder="지점 이름 또는 코드"
              aria-label="현재 페이지에서 지점 찾기"
            />
          </div>
        }
      />
      <ListPage.Body role="region" aria-label="지점 목록" className="min-w-0 border-t">
        <DataTable
          columns={columns}
          data={visibleProperties}
          rowKey={(property) => property.property_id}
          onRowClick={(property) => {
            goDetail(property.property_id);
          }}
          empty={{
            title: normalizedPropertySearch
              ? '현재 페이지에 조건에 맞는 지점이 없습니다'
              : state.propertyCursor
                ? '이 지점 조회 위치에 결과가 없습니다'
                : '조회된 지점이 없습니다',
          }}
        />
      </ListPage.Body>
      {(hasNext || hasPrevious) && (
        <ListPage.Pagination className="border-t px-[var(--layout-gutter)] py-2">
          <nav
            aria-label="지점 페이지 이동"
            className="flex w-full flex-wrap items-center justify-between gap-3"
          >
            <span className="caption text-muted-foreground" aria-live="polite">
              {normalizedPropertySearch
                ? `${String(visibleProperties.length)}건 검색됨 (${String(rows.length)}건 중)`
                : `${String(rows.length)}건 표시`}
            </span>
            <div className="flex items-center gap-2">
              {hasPrevious && (
                <Button
                  nativeButton={false}
                  role="link"
                  variant="ghost"
                  render={<Link to="/access" />}
                >
                  처음으로
                </Button>
              )}
              <Button
                variant="outline"
                disabled={!hasPrevious || list.isFetching}
                onClick={() => {
                  if (cameFromList) void navigate(-1);
                  else void navigate('/access');
                }}
              >
                <ChevronLeft aria-hidden />
                이전
              </Button>
              <Button variant="outline" disabled={!hasNext || list.isFetching} onClick={next}>
                다음
                <ChevronRight aria-hidden />
              </Button>
            </div>
          </nav>
        </ListPage.Pagination>
      )}
    </ListPage>
  );
}

function permissionKindLabel(kind: string) {
  switch (kind) {
    case 'ROOM_ENTRY':
      return '객실 출입';
    case 'PROPERTY_ROUTE':
      return '지점 동선';
    case 'ELEVATOR_FLOOR':
      return '승강기 층';
    default:
      return '권한 설정';
  }
}

const roomColumns = (
  page: import('@/api/core/properties').PropertyCatalog,
  slotsByRoom: Map<string, CatalogSlot[]>,
  onSelect: (room: CatalogRoom) => void,
): DataTableColumn<CatalogRoom>[] => [
  {
    key: 'roomNo',
    sortable: true,
    header: '호실',
    cell: (room) => (
      <button
        type="button"
        className="text-primary underline-offset-4 hover:underline"
        onClick={() => {
          onSelect(room);
        }}
      >
        {room.roomNo}호
      </button>
    ),
  },
  {
    key: 'floor',
    sortable: true,
    header: '층',
    cell: (room) => (room.floor === null ? '정보 없음' : `${String(room.floor)}층`),
  },
  {
    key: 'typeName',
    sortable: true,
    header: '객실 유형',
    cell: (room) => room.typeName ?? '정보 없음',
  },
  {
    key: 'slots',
    sortable: false,
    header: '연결 슬롯',
    cell: (room) => {
      const slots = slotsByRoom.get(room.id) ?? [];
      return slots.length ? (
        <span>
          {slots
            .map((slot) => {
              const name = canonicalSlotName(page, slot.id) ?? '이름 미등록';
              return slot.slotNo ? `${name} · ${slot.slotNo}` : name;
            })
            .join(', ')}
        </span>
      ) : (
        '없음'
      );
    },
  },
];

export function AccessPropertyDetailPage({
  runtime = propertyRuntime,
}: { runtime?: typeof propertyRuntime } = {}) {
  const { propertyId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const [roomSort, setRoomSort] = useState<string | null>(null);
  const state = accessUrlState(params);
  const validProperty = isUuid(propertyId);
  const validDetailState = state.valid && state.propertyCursor === null;
  const catalog = useQuery(
    propertyCatalogQuery(
      runtime,
      session.generation,
      propertyId,
      { room: state.roomCursor },
      validProperty && validDetailState && session.status === 'authorized',
      () => {
        const current = activeRoomRead.get(runtime.session);
        return (
          current?.propertyId === propertyId &&
          current.roomCursor === state.roomCursor &&
          current.generation === session.generation
        );
      },
    ),
  );
  const page =
    catalog.data?.property.property_id.toLowerCase() === propertyId.toLowerCase()
      ? catalog.data
      : undefined;
  const scope = `${propertyId}:${state.roomCursor ?? ''}`;
  const [filters, setFilters] = useState({ scope, search: '', floor: 'all', type: 'all' });
  const activeFilters =
    filters.scope === scope ? filters : { scope, search: '', floor: 'all', type: 'all' };
  const selectedRoom =
    page?.rooms.find((room) => room.id.toLowerCase() === state.roomId?.toLowerCase()) ?? null;
  const onlineAccess = useQuery(
    roomOnlineAccessQuery(
      runtime,
      session.generation,
      propertyId,
      selectedRoom?.id ?? null,
      validProperty &&
        validDetailState &&
        !!page &&
        !!selectedRoom &&
        session.status === 'authorized',
      () => {
        const current = activeRoomRead.get(runtime.session);
        if (!current) return false;
        return (
          current.propertyId === propertyId &&
          current.roomId?.toLowerCase() === selectedRoom?.id.toLowerCase() &&
          current.generation === session.generation
        );
      },
    ),
  );
  const onlineAccessRefreshFlight = useRef(false);
  const [onlineAccessRetryPending, setOnlineAccessRetryPending] = useState(false);
  const refreshOnlineAccess = () => {
    if (onlineAccessRefreshFlight.current || onlineAccess.isFetching) return;
    onlineAccessRefreshFlight.current = true;
    setOnlineAccessRetryPending(true);
    void onlineAccess.refetch({ cancelRefetch: false }).finally(() => {
      onlineAccessRefreshFlight.current = false;
      setOnlineAccessRetryPending(false);
    });
  };
  const inventoryScope = `${runtime.apiNamespace}:${String(session.generation)}:${propertyId}:${page?.property.environment ?? ''}:${String(page?.property.branch_id ?? '')}:${state.roomCursor ?? ''}:${selectedRoom?.id ?? ''}`;
  const [inventoryFilters, setInventoryFilters] = useState({
    scope: inventoryScope,
    slotId: null as string | null,
    cursors: [null] as (string | null)[],
  });
  const currentInventoryFilters =
    inventoryFilters.scope === inventoryScope
      ? inventoryFilters
      : { scope: inventoryScope, slotId: null, cursors: [null] as (string | null)[] };
  const inventoryCursor = currentInventoryFilters.cursors.at(-1) ?? null;
  const inventoryEnabled =
    validProperty &&
    validDetailState &&
    !!page &&
    !!selectedRoom &&
    session.status === 'authorized';
  const currentInventoryRead: ActiveInventoryRead | null = selectedRoom
    ? {
        apiNamespace: runtime.apiNamespace,
        propertyId,
        environment: page?.property.environment ?? 'dev',
        branchId: page?.property.branch_id ?? -1,
        roomId: selectedRoom.id,
        catalogCursor: state.roomCursor,
        slotId: currentInventoryFilters.slotId,
        cursor: inventoryCursor,
        generation: session.generation,
      }
    : null;
  const inventoryQueryOptions = coreAccessDeviceInventoryQuery(
    runtime,
    session.generation,
    propertyId,
    page?.property.environment ?? 'dev',
    page?.property.branch_id ?? -1,
    selectedRoom?.id ?? '',
    {
      roomId: selectedRoom?.id,
      slotId: currentInventoryFilters.slotId ?? undefined,
      cursor: inventoryCursor ?? undefined,
      limit: 50,
    },
    inventoryEnabled,
    () => {
      const current = activeInventoryRead.get(runtime.session);
      return (
        !!current &&
        !!currentInventoryRead &&
        current.propertyId === currentInventoryRead.propertyId &&
        current.apiNamespace === currentInventoryRead.apiNamespace &&
        current.environment === currentInventoryRead.environment &&
        current.branchId === currentInventoryRead.branchId &&
        current.roomId.toLowerCase() === currentInventoryRead.roomId.toLowerCase() &&
        current.catalogCursor === currentInventoryRead.catalogCursor &&
        current.slotId?.toLowerCase() === currentInventoryRead.slotId?.toLowerCase() &&
        current.cursor?.toLowerCase() === currentInventoryRead.cursor?.toLowerCase() &&
        current.generation === currentInventoryRead.generation
      );
    },
  );
  const inventory = useQuery(inventoryQueryOptions);
  useEffect(() => {
    const legacyKey = ['core', runtime.apiNamespace, session.generation, 'access-device-inventory'];
    const matchesLegacy = (query: { queryKey: readonly unknown[] }) =>
      legacyKey.every((part, index) => query.queryKey[index] === part);
    void queryClient.cancelQueries({ predicate: matchesLegacy });
    queryClient.removeQueries({ predicate: matchesLegacy });
  }, [queryClient, runtime.apiNamespace, session.generation]);
  const nextInventoryCursor = inventory.data?.next_cursor ?? null;
  const inventoryRefreshFlight = useRef(false);
  const refreshInventory = () => {
    if (inventoryRefreshFlight.current || inventory.isFetching) return;
    inventoryRefreshFlight.current = true;
    void inventory.refetch({ cancelRefetch: false }).finally(() => {
      inventoryRefreshFlight.current = false;
    });
  };
  useInsertionEffect(() => {
    const current = {
      propertyId,
      roomId: selectedRoom?.id ?? null,
      roomCursor: state.roomCursor,
      generation: session.generation,
    };
    activeRoomRead.set(runtime.session, current);
    if (currentInventoryRead) activeInventoryRead.set(runtime.session, currentInventoryRead);
    return () => {
      if (activeRoomRead.get(runtime.session) === current) activeRoomRead.delete(runtime.session);
      if (currentInventoryRead && activeInventoryRead.get(runtime.session) === currentInventoryRead)
        activeInventoryRead.delete(runtime.session);
    };
  }, [
    runtime.session,
    runtime.apiNamespace,
    propertyId,
    selectedRoom?.id,
    state.roomCursor,
    session.generation,
    currentInventoryRead?.environment,
    currentInventoryRead?.branchId,
    currentInventoryRead?.slotId,
    currentInventoryRead?.cursor,
  ]);
  if (session.status !== 'authorized') return null;
  if (!validProperty || !validDetailState)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <div role="alert" className="p-6">
          <p>조회 주소가 올바르지 않습니다.</p>
          <Button
            variant="outline"
            onClick={() => {
              void navigate('/access');
            }}
          >
            지점 목록으로
          </Button>
        </div>
      </ListPage>
    );
  if (catalog.isError)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <AccessFailure
          error={catalog.error}
          onRetry={() => {
            void catalog.refetch();
          }}
        />
      </ListPage>
    );
  if (!page)
    return (
      <ListPage>
        <ShellHeader>
          <PageHeader title="출입 관리" />
        </ShellHeader>
        <p role="status" className="p-6">
          {catalog.isPending ? '객실 자료를 불러오고 있습니다.' : '지점 자료를 확인할 수 없습니다.'}
        </p>
      </ListPage>
    );
  const locationState = location.state as { from?: unknown } | null;
  const back = safeAccessListReturn(locationState?.from);
  const slotsByRoom = new Map<string, CatalogSlot[]>();
  for (const slot of page.slots)
    slotsByRoom.set(slot.roomId, [...(slotsByRoom.get(slot.roomId) ?? []), slot]);
  const normalizedSearch = activeFilters.search.trim().toLocaleLowerCase();
  const visibleRooms = page.rooms.filter(
    (room) =>
      (!normalizedSearch ||
        `${String(room.roomNo)} ${room.typeName ?? ''}`
          .toLocaleLowerCase()
          .includes(normalizedSearch)) &&
      (activeFilters.floor === 'all' ||
        (activeFilters.floor === 'null'
          ? room.floor === null
          : room.floor === Number(activeFilters.floor))) &&
      (activeFilters.type === 'all' || String(room.typeId) === activeFilters.type),
  );
  const [sortKey, sortDir] = (roomSort ?? '').split(',');
  const sortedRooms =
    sortKey === 'roomNo' || sortKey === 'floor' || sortKey === 'typeName'
      ? [...visibleRooms].sort((a, b) => {
          const left = a[sortKey];
          const right = b[sortKey];
          // 값이 없는 행은 방향과 무관하게 항상 뒤로 보낸다.
          if (left === null || right === null)
            return Number(left === null) - Number(right === null);
          const order =
            typeof left === 'number' && typeof right === 'number'
              ? left - right
              : String(left).localeCompare(String(right), 'ko', { numeric: true });
          return sortDir === 'desc' ? -order : order;
        })
      : visibleRooms;
  const setRoom = (room: CatalogRoom | null) => {
    setInventoryFilters({
      scope: `${runtime.apiNamespace}:${String(session.generation)}:${propertyId}:${page.property.environment}:${String(page.property.branch_id)}:${state.roomCursor ?? ''}:${room?.id ?? ''}`,
      slotId: null,
      cursors: [null],
    });
    const next = new URLSearchParams(params);
    if (room) next.set('room', room.id);
    else next.delete('room');
    setParams(next);
  };
  const setRoomCursor = (cursor: string | null) => {
    const next = new URLSearchParams();
    if (cursor) next.set('room_cursor', cursor);
    setParams(next);
  };
  const floors = [...new Set(page.rooms.map((room) => room.floor))];
  const types = [
    ...new Map(
      page.rooms.map((room) => [room.typeId, room.typeName ?? `유형 ${String(room.typeId)}`]),
    ).entries(),
  ];
  const cols = roomColumns(page, slotsByRoom, setRoom);
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title={`출입 관리 · ${page.property.name}`}
          description="객실과 온라인 출입 설정을 확인합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      <ListPage.Body className="min-w-0 space-y-4 p-4 sm:p-6">
        <div className="min-w-0 space-y-2 rounded-md border p-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <p className="body-strong min-w-0 break-words">현재 지점 · {page.property.name}</p>
            <nav aria-label="지점 이동" className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                nativeButton={false}
                role="link"
                render={<Link to={back}>지점 목록</Link>}
              />
              <Button
                variant="outline"
                nativeButton={false}
                role="link"
                render={
                  <Link to={`/properties/legacy/${page.property.property_id}`}>지점 상세</Link>
                }
              />
            </nav>
          </div>
          <details className="caption text-muted-foreground">
            <summary className="cursor-pointer">지점 식별 정보</summary>
            <p className="mt-1 break-all">{page.property.property_id}</p>
          </details>
        </div>
        <section aria-label="객실 목록" className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-48 flex-1 flex-col gap-1">
              <span className="caption">현재 객실 페이지 검색</span>
              <Input
                value={activeFilters.search}
                onChange={(event) => {
                  setFilters({ ...activeFilters, search: event.target.value });
                }}
                placeholder="호실 또는 객실 유형"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="caption">층</span>
              <Select
                value={activeFilters.floor}
                onValueChange={(value) => {
                  setFilters({ ...activeFilters, floor: value ?? 'all' });
                }}
              >
                <SelectTrigger aria-label="층">
                  <SelectValue>
                    {(value) =>
                      value === 'all'
                        ? '모든 층'
                        : value === 'null'
                          ? '층 정보 없음'
                          : `${String(value)}층`
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">모든 층</SelectItem>
                  {floors.map((value) => (
                    <SelectItem key={String(value)} value={value === null ? 'null' : String(value)}>
                      {value === null ? '층 정보 없음' : `${String(value)}층`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="caption">객실 유형</span>
              <Select
                value={activeFilters.type}
                onValueChange={(value) => {
                  setFilters({ ...activeFilters, type: value ?? 'all' });
                }}
              >
                <SelectTrigger aria-label="객실 유형">
                  <SelectValue>
                    {(value) =>
                      value === 'all'
                        ? '모든 유형'
                        : (types.find(([id]) => String(id) === String(value))?.[1] ?? '모든 유형')
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">모든 유형</SelectItem>
                  {types.map(([id, name]) => (
                    <SelectItem key={id} value={String(id)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
          <p className="caption text-muted-foreground">
            객실 목록은 좁은 화면에서 좌우로 이동할 수 있습니다. 정렬은 현재 불러온 페이지에만
            적용됩니다.
          </p>
          <div
            className="min-w-0 overflow-x-auto"
            role="region"
            aria-label="객실·슬롯 표"
            tabIndex={0}
          >
            <div className="min-w-[40rem]">
              <DataTable
                columns={cols}
                data={sortedRooms}
                sort={roomSort}
                onSortChange={setRoomSort}
                rowKey={(room) => room.id}
                empty={{
                  title:
                    page.rooms.length > 0 && visibleRooms.length === 0
                      ? '조건에 맞는 객실이 없습니다'
                      : '객실이 없습니다',
                  description: '현재 불러온 객실만 표시합니다.',
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
            <p className="caption text-muted-foreground">
              현재 페이지 {page.rooms.length}개 / 전체 {page.totalRoomCount}개 객실 · 슬롯{' '}
              {page.slots.length}개
            </p>
            <nav aria-label="객실 페이지 이동" className="flex flex-wrap gap-2">
              {state.roomCursor && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setRoomCursor(null);
                  }}
                >
                  객실 첫 페이지
                </Button>
              )}
              {page.roomNextCursor && (
                <Button
                  variant="outline"
                  disabled={catalog.isFetching}
                  onClick={() => {
                    setRoomCursor(page.roomNextCursor);
                  }}
                >
                  다음 객실 페이지
                </Button>
              )}
            </nav>
          </div>
        </section>
        {state.roomId && !selectedRoom && (
          <p role="status" className="rounded-md border p-4">
            선택한 객실은 현재 불러온 페이지에 없습니다. 해당 객실이 있는 페이지를 불러오면 설정을
            확인할 수 있습니다.
          </p>
        )}
        {selectedRoom && (
          <section
            aria-label={`${String(selectedRoom.roomNo)}호 객실 상세`}
            className="min-w-0 space-y-4 rounded-md border p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="title-sm">
                {String(selectedRoom.roomNo)}호 · {selectedRoom.typeName ?? '객실 유형 정보 없음'}
              </h2>
              <Button
                variant="outline"
                onClick={() => {
                  setRoom(null);
                }}
              >
                선택 해제
              </Button>
            </div>
            <div>
              <h3 className="body-strong">
                연결 슬롯 ({(slotsByRoom.get(selectedRoom.id) ?? []).length}개)
              </h3>
              {(slotsByRoom.get(selectedRoom.id) ?? []).length ? (
                <ul className="mt-2 flex flex-wrap gap-2">
                  {(slotsByRoom.get(selectedRoom.id) ?? []).map((slot) => (
                    <li key={slot.id} className="rounded border px-3 py-2">
                      {canonicalSlotName(page, slot.id) ?? '이름 미등록'} · {slot.slotNo}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="body text-muted-foreground mt-1">연결된 슬롯이 없습니다.</p>
              )}
            </div>
            <section aria-label="장비 연결 기록" className="min-w-0 border-t pt-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="body-strong">장비 연결 기록</h3>
                  <p className="caption text-muted-foreground mt-1">
                    선택한 객실과 슬롯에 저장된 연결 정보입니다. 장비의 실제 작동 상태는 확인하지
                    않습니다.
                  </p>
                </div>
                <Button
                  variant="outline"
                  disabled={inventory.isFetching}
                  onClick={refreshInventory}
                >
                  {inventory.isFetching ? '확인 중' : '다시 조회'}
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                  <span className="caption">슬롯</span>
                  <Select
                    value={currentInventoryFilters.slotId ?? 'all'}
                    onValueChange={(value) => {
                      setInventoryFilters({
                        scope: inventoryScope,
                        slotId: value === 'all' ? null : value,
                        cursors: [null],
                      });
                    }}
                    disabled={inventory.isFetching}
                  >
                    <SelectTrigger aria-label="장비 연결 슬롯">
                      <SelectValue>
                        {(value) =>
                          value === 'all'
                            ? '모든 슬롯'
                            : (() => {
                                const slot = page.slots.find(
                                  (candidate) =>
                                    candidate.roomId.toLowerCase() ===
                                      selectedRoom.id.toLowerCase() &&
                                    candidate.id.toLowerCase() === String(value).toLowerCase(),
                                );
                                return slot
                                  ? `${canonicalSlotName(page, slot.id) ?? `슬롯 ${slot.slotNo}`} · ${slot.slotNo}`
                                  : '모든 슬롯';
                              })()
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">모든 슬롯</SelectItem>
                      {(slotsByRoom.get(selectedRoom.id) ?? []).map((slot) => (
                        <SelectItem key={slot.id} value={slot.id}>
                          {canonicalSlotName(page, slot.id) ?? `슬롯 ${slot.slotNo}`} ·{' '}
                          {slot.slotNo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <p className="caption text-muted-foreground">
                  {inventory.data
                    ? `연결 정보 조회 ${new Date(inventory.data.observed_at).toLocaleString('ko-KR')}`
                    : '조회 대기'}
                </p>
                <p className="caption text-muted-foreground">
                  조회 시각이며 원본 장비 기록 시각과 다를 수 있습니다.
                </p>
                {inventory.isFetching && inventory.data && (
                  <p role="status" className="caption text-muted-foreground">
                    확인 중 · 직전 조회{' '}
                    {new Date(inventory.data.observed_at).toLocaleString('ko-KR')}
                  </p>
                )}
              </div>
              {inventory.isError ? (
                isApiError(inventory.error) && inventory.error.status === 400 && inventoryCursor ? (
                  <div
                    role="alert"
                    className="mt-3 flex flex-wrap items-center gap-3 rounded-md border p-3"
                  >
                    <p>조회 위치를 확인하지 못했습니다. 첫 페이지부터 다시 조회해 주세요.</p>
                    <Button
                      variant="outline"
                      disabled={inventory.isFetching}
                      onClick={() => {
                        setInventoryFilters({ ...currentInventoryFilters, cursors: [null] });
                      }}
                    >
                      첫 페이지부터 다시 조회
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3">
                    <AccessFailure
                      error={inventory.error}
                      onRetry={refreshInventory}
                      busy={inventory.isFetching}
                      message={
                        isApiError(inventory.error) && inventory.error.status === 404
                          ? '선택한 객실 또는 슬롯을 찾을 수 없습니다'
                          : isApiError(inventory.error) &&
                              (inventory.error.status === 503 ||
                                inventory.error.kind === 'timeout' ||
                                inventory.error.kind === 'network')
                            ? '장비 관계를 확인하지 못했습니다'
                            : undefined
                      }
                    />
                  </div>
                )
              ) : inventory.isPending ? (
                <p role="status" className="body text-muted-foreground mt-3">
                  장비 연결 기록을 확인하고 있습니다.
                </p>
              ) : (
                <>
                  {inventoryBranchStateLabel(inventory.data.source_state) && (
                    <p className="body mt-3" role="status">
                      {inventoryBranchStateLabel(inventory.data.source_state)}
                    </p>
                  )}
                  {inventory.data.source_state === 'OBSERVED' &&
                  inventory.data.items.length === 0 ? (
                    <p className="body text-muted-foreground mt-3">
                      {currentInventoryFilters.slotId
                        ? '선택한 슬롯에 조회할 연결 기록이 없습니다.'
                        : inventoryCursor
                          ? '다음 슬롯이 없습니다.'
                          : '이 객실에 조회할 슬롯이 없습니다.'}
                    </p>
                  ) : inventory.data.items.length > 0 ? (
                    <div className="min-w-0" style={{ overflowX: 'clip' }}>
                      <p
                        id="inventory-scroll-hint"
                        className="caption text-muted-foreground mt-3 sm:hidden"
                      >
                        표는 좌우로 스크롤할 수 있습니다.
                      </p>
                      <div
                        className="mt-3 min-w-0 overflow-x-auto"
                        role="region"
                        aria-label="슬롯별 장비 연결 표"
                        aria-describedby="inventory-scroll-hint"
                        tabIndex={0}
                      >
                        <div style={{ minWidth: '44rem' }}>
                          <DataTable
                            columns={
                              [
                                {
                                  key: 'slot',
                                  sortable: false,
                                  header: '슬롯',
                                  cell: (item: CoreAccessDeviceInventoryItem) => {
                                    const catalogSlot = page.slots.find(
                                      (slot) =>
                                        slot.id.toLowerCase() === item.slot_id.toLowerCase() &&
                                        slot.roomId.toLowerCase() === selectedRoom.id.toLowerCase(),
                                    );
                                    const sourceSlot = inventory.data.core_slots.find(
                                      (slot) =>
                                        slot.slot_id.toLowerCase() === item.slot_id.toLowerCase() &&
                                        slot.room_id.toLowerCase() ===
                                          selectedRoom.id.toLowerCase(),
                                    );
                                    const slotName = catalogSlot
                                      ? `${canonicalSlotName(page, catalogSlot.id) ?? `슬롯 ${catalogSlot.slotNo}`} · ${catalogSlot.slotNo}`
                                      : sourceSlot
                                        ? `슬롯 ${sourceSlot.source_slot_no || '식별 정보 없음'} · 원본 슬롯 ID ${String(sourceSlot.source_slot_id)}`
                                        : '슬롯 식별 정보 없음';
                                    return slotName;
                                  },
                                },
                                {
                                  key: 'state',
                                  sortable: false,
                                  header: '연결 기록',
                                  cell: (item: CoreAccessDeviceInventoryItem) =>
                                    inventoryStateLabel(item.state),
                                },
                                {
                                  key: 'device',
                                  sortable: false,
                                  header: '장비 정보',
                                  cell: (item: CoreAccessDeviceInventoryItem) =>
                                    item.room_id.toLowerCase() !== selectedRoom.id.toLowerCase() ||
                                    !item.device ? (
                                      '정보 없음'
                                    ) : (
                                      <details>
                                        <summary className="cursor-pointer">
                                          {item.device.device_id}
                                        </summary>
                                        <dl className="caption mt-2 grid gap-1 break-all">
                                          <div>
                                            <dt>장비 ID</dt>
                                            <dd>{item.device.device_id}</dd>
                                          </div>
                                          <div>
                                            <dt>원본 도어락 ID</dt>
                                            <dd>{item.device.source_doorlock_id ?? '없음'}</dd>
                                          </div>
                                          <div>
                                            <dt>게이트웨이 ID</dt>
                                            <dd>{item.device.gateway_id}</dd>
                                          </div>
                                          <div>
                                            <dt>장비 유형</dt>
                                            <dd>{item.device.device_type ?? '정보 없음'}</dd>
                                          </div>
                                          <div>
                                            <dt>저장된 활성 설정</dt>
                                            <dd>
                                              {item.device.is_active ? '활성' : '비활성'} · 연결
                                              상태 미확인
                                            </dd>
                                          </div>
                                          <div>
                                            <dt>연동 지원</dt>
                                            <dd>장비 연동 확인 전</dd>
                                          </div>
                                          <div>
                                            <dt>Core UUID</dt>
                                            <dd>{item.device.core_device_id ?? '정보 없음'}</dd>
                                          </div>
                                          <div>
                                            <dt>메타데이터 revision</dt>
                                            <dd>{item.device.revision ?? '정보 없음'}</dd>
                                          </div>
                                        </dl>
                                      </details>
                                    ),
                                },
                              ] satisfies DataTableColumn<CoreAccessDeviceInventoryItem>[]
                            }
                            data={inventory.data.items}
                            rowKey={(item) => item.slot_id}
                            empty={{ title: '표시할 연결 기록이 없습니다.' }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <nav aria-label="장비 연결 페이지 이동" className="mt-3 flex flex-wrap gap-2">
                    {currentInventoryFilters.cursors.length > 1 && (
                      <Button
                        variant="outline"
                        disabled={inventory.isFetching}
                        onClick={() => {
                          setInventoryFilters({
                            ...currentInventoryFilters,
                            cursors: currentInventoryFilters.cursors.slice(0, -1),
                          });
                        }}
                      >
                        이전 슬롯
                      </Button>
                    )}
                    {nextInventoryCursor && (
                      <Button
                        variant="outline"
                        disabled={inventory.isFetching}
                        onClick={() => {
                          setInventoryFilters({
                            ...currentInventoryFilters,
                            cursors: [...currentInventoryFilters.cursors, nextInventoryCursor],
                          });
                        }}
                      >
                        다음 슬롯
                      </Button>
                    )}
                  </nav>
                </>
              )}
            </section>
            {session.authorization &&
              canWriteProperties(session.authorization) &&
              !onlineAccess.isError &&
              !onlineAccess.isPending && (
                <RoomOnlineAccessSection
                  initialSnapshot={onlineAccess.data}
                  onSaved={refreshOnlineAccess}
                  runtime={runtime}
                  generation={session.generation}
                  propertyId={propertyId}
                  roomId={selectedRoom.id}
                  isCurrent={() => {
                    const current = activeRoomRead.get(runtime.session);
                    return (
                      current?.propertyId === propertyId &&
                      current.roomId === selectedRoom.id &&
                      current.generation === session.generation
                    );
                  }}
                />
              )}
            <div aria-live="polite" className="border-t pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="body-strong">저장된 온라인 출입 설정</h3>
                <Button
                  variant="outline"
                  disabled={onlineAccess.isFetching || onlineAccessRetryPending}
                  onClick={refreshOnlineAccess}
                >
                  {onlineAccess.isFetching || onlineAccessRetryPending
                    ? '설정 조회 중'
                    : '설정 다시 조회'}
                </Button>
              </div>
              {onlineAccess.isError ? (
                <AccessFailure
                  error={onlineAccess.error}
                  onRetry={refreshOnlineAccess}
                  busy={onlineAccess.isFetching || onlineAccessRetryPending}
                />
              ) : onlineAccess.isPending ? (
                <p role="status" className="body text-muted-foreground mt-2">
                  설정을 확인하고 있습니다.
                </p>
              ) : onlineAccess.data === null ? (
                <p className="body text-muted-foreground mt-2">
                  저장된 온라인 출입 설정이 없습니다.
                </p>
              ) : (
                <div className="mt-2 space-y-3">
                  <p className="body-strong">
                    {onlineAccess.data.status === 'RETIRED'
                      ? '설정 종료됨'
                      : '설정 저장됨 · 사용 가능 여부 미확인'}
                  </p>
                  {onlineAccess.data.status === 'UNVERIFIED' ? (
                    <p className="body">
                      {onlineAccess.data.issue_eligible
                        ? '발급 가능 여부를 확인하지 못했습니다.'
                        : '이 설정만으로 발급할 수 없습니다.'}{' '}
                      이 설정은 장비 적용이나 실제 출입을 확인하지 않습니다.
                    </p>
                  ) : (
                    <p className="body">
                      설정 종료는 기존 자격이나 장비 적용의 회수 여부를 뜻하지 않습니다.
                    </p>
                  )}
                  <p className="caption text-muted-foreground">
                    설정 기록 시각 {new Date(onlineAccess.data.updated_at).toLocaleString('ko-KR')}
                  </p>
                  <ul className="space-y-1">
                    {onlineAccess.data.permission_items.map((item, index) => (
                      <li
                        key={`${item.kind}-${item.target_namespace}-${String(index)}`}
                        className="body"
                      >
                        {permissionKindLabel(item.kind)} · 대상 이름 미제공
                      </li>
                    ))}
                  </ul>
                  <details>
                    <summary className="caption cursor-pointer">기술 정보</summary>
                    <dl className="caption mt-2 grid gap-1 break-all">
                      <div>
                        <dt>설정 참조</dt>
                        <dd>{onlineAccess.data.binding_id}</dd>
                      </div>
                      <div>
                        <dt>기록 사유</dt>
                        <dd>{onlineAccess.data.reason}</dd>
                      </div>
                      <div>
                        {onlineAccess.data.permission_items.map((item, index) => (
                          <p key={String(index)}>
                            {item.target_namespace}: {item.target_ref}
                          </p>
                        ))}
                      </div>
                    </dl>
                  </details>
                </div>
              )}
            </div>
          </section>
        )}
      </ListPage.Body>
    </ListPage>
  );
}
